<template>
  <div>
    <v-stepper class="elevation-0" v-model="stepperStep">
      <StepperHeader :stepperStep="stepperStep" :offerType="offer.type" />

      <v-divider />
    </v-stepper>

    <v-stepper class="elevation-0 mb-12 pb-12" v-model="offer.step">
      <v-container class="pt-0">
        <Alerts />

        <v-stepper-items>
          <v-stepper-content v-for="stp in steps" :key="stp" :step="stp" class="px-0">
            <div v-if="Number(stp) === Number(offer.step)">
              <component
                :is="stepperComponents[stp]"
                :infoRequiredDialog="infoRequiredDialog"
                @onChangeIsContinueForbidden="onChangeIsContinueForbidden"
                @onSaveClicked="() => saveOffer(true)"
                @onResetClicked="resetDialog = true"
                @onChangeIsLoading="onChangeIsLoading"
              />
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-container>

      <ResetOfferDialog
        :show="resetDialog"
        @cancelResetOffer="resetDialog = false"
        @confirmResetOffer="resetOffer"
      />

      <ManualSignDialog
        :show="manualSignDialog"
        @cancelManualSign="manualSignDialog = false"
        @confirmManualSign="handleManualSignBtnClick"
      />

      <EditValidatedOfferDialog
        :show="showEditValidatedOfferDialog"
        @cancelEditValidatedOffer="handleCancelEditValidatedOffer"
        @confirmEditValidatedOffer="handleConfirmEditValidatedOffer"
      />
      
      <FinalizeOfferDialog
        :show="finalizeOfferDialog"
        @cancelFinalizeOffer="finalizeOfferDialog = false"
        @confirmFinalizeOffer="proceedWithFinalization"
      />
    </v-stepper>

    <!-- FOOTER -->
    <StepperFooter
      :ctaBtn="ctaBtn"
      :manualSignBtn="manualSignBtn"
      @onClickPrevBtn="prevStep"
      @onClickCtaBtn="handleCtaBtnClick"
      @onClickManualSignBtn="manualSignDialog = true"
    />
  </div>
</template>

<script>
import StepInit from '@/containers/offer-form/step-init'
import Products from '@/containers/offer-form/products'
import Shipping from '@/containers/offer-form/shipping'
import AdditionalServices from '@/containers/offer-form/additional-services'
import PaymentMethods from '@/containers/offer-form/payment-methods'
import Packages from '@/containers/offer-form/packages'
import Dispersion from '@/containers/offer-form/dispersion'
import SignOff from '@/containers/offer-form/sign-off'
import TypeFSignOff from '@/containers/offer-form/sign-off-type-f'
import ResetOfferDialog from '@/containers/offer-form/ResetOfferDialog'
import ManualSignDialog from '@/containers/offer-form/ManualSignDialog'
import EditValidatedOfferDialog from '@/containers/offer-form/EditValidatedOfferDialog'
import FinalizeOfferDialog from '@/containers/offer-form/FinalizeOfferDialog'
import StepperHeader from '@/components/StepperHeader'
import StepperFooter from '@/components/stepper-footer'
import Alerts from '@/components/Alerts'
import {
  STEPPER_BASE_COMPONENTS,
  STEPPER_ADVANCED_COMPONENTS,
  STEPPER_PHANTOM_COMPONENTS,
  OFFER_STATUS,
  CLIENT_DATA_VALIDATION_SCHEMA,
  DEBIT_BANK_DATA_VALIDATION_SCHEMA,
  REFUND_BANK_DATA_VALIDATION_SCHEMA,
  DEBIT_PAYMENT_METHODS,
  CONTRACT_MODEL,
  OFFER_TYPE,
} from '@/helpers/constants'
import { isSchemaValid } from '@/helpers/schemaValidation'
import { calculateOfferTypeByValidations, getComputedOfferType } from '@/helpers/offerTypes'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import {
  UPDATE_AND_SEND_OFFER,
  UPDATE_OFFER_BASEPRICE_ITEM,
  UPDATE_OFFER_DISCOUNT,
  UPDATE_OFFER,
  RESET_OFFER,
  SET_OFFER_TARIFF_MODEL,
  SEND_CONTRACT_TO_SIGN,
  MANUAL_SIGN,
  REQUEST_CONTRACT_STATUS,
  REQUEST_OFFER_DATA,
  CANCEL_EDIT_VALIDATED_OFFER,
  CONFIRM_EDIT_VALIDATED_OFFER,
} from '@/store/modules/offer/actions'
import { SET_OFFER_ITEM, SET_TYPE_AND_VAL_STATUS_OFFER } from '@/store/modules/offer/mutations'
import { GET_CONTRACT_MODEL } from '@/store/modules/offer/getters'

export default {
  name: 'OfferForm',
  components: {
    StepInit,
    Products,
    Shipping,
    AdditionalServices,
    PaymentMethods,
    Packages,
    Dispersion,
    SignOff,
    TypeFSignOff,
    StepperHeader,
    StepperFooter,
    Alerts,
    ResetOfferDialog,
    ManualSignDialog,
    EditValidatedOfferDialog,
    FinalizeOfferDialog,
  },
  data() {
    return {
      isContinueForbidden: false,
      isLoading: false,
      infoRequiredDialog: false,
      resetDialog: false,
      manualSignDialog: false,
      finalizeOfferDialog: false,
    }
  },
  provide() {
    return {
      closeInfoRequiredDialog: this.closeInfoRequiredDialog,
      increaseOfferStep: this.increaseOfferStep,
    }
  },
  computed: {
    ...mapState('offer', ['offer']),
    ...mapGetters('offer', ['canEditOffer']),
    ...mapGetters('offer', { contractModel: GET_CONTRACT_MODEL }),
    ...mapGetters('rateModel', ['isTypeBroker', 'isTypeF']),
    ...mapState('process', ['pdfFormData', 'showEditValidatedOfferDialog']),
    ...mapGetters('process', ['isStep1ReadOnly']),
    stepperStep() {
      return Math.floor(this.offer.step)
    },
    isTypeAOffer() {
      return this.offer.type === OFFER_TYPE.A
    },
    isTypeFOffer() {
      return this.offer.type === OFFER_TYPE.F
    },
    steps() {
      return Object.keys(this.stepperComponents)
        .map(key => Number(key))
        .sort()
    },
    stepsLength() {
      if (this.isTypeFOffer) {
        return this.steps?.length ?? 0
      }
      // - 1 in order to remove the 2.5 substep
      return this.steps?.length - 1 ?? 0
    },
    hasSignatureProcessStarted() {
      return this.offer.status === OFFER_STATUS.SIGNING || this.offer.status === OFFER_STATUS.SIGNED
    },
    isContractModelValid() {
      return Object.values(CONTRACT_MODEL).includes(this.contractModel)
    },
    shouldSendOffer() {
      return (
        (this.offer.status === OFFER_STATUS.EDITED ||
          this.offer.status === OFFER_STATUS.SYNCHRONIZED) &&
        !this.isTypeFOffer
      )
    },
    shouldSendFOffer() {
      return this.isTypeF && this.offer.status === OFFER_STATUS.CREATED;
    },
    isLastStep() {
      return this.offer.step === this.stepsLength
    },
    isPenultimateStep() {
      return this.offer.step === this.stepsLength - 1
    },
    isTypeFOfferLastStep() {
      return this.isTypeFOffer && this.isLastStep
    },
    ctaBtn() {
      let type = 'continue'
      if (this.isLastStep) {
        type = 'sign'
      } else if (this.isPenultimateStep && !this.hasSignatureProcessStarted && !this.isTypeFOffer) {
        type = 'end'
      }
      return {
        show: !this.isTypeFOfferLastStep,
        loading: this.isLoading,
        disabled:
          this.isContinueForbidden ||
          this.isLoading ||
          (this.isLastStep && (!this.isContractModelValid || this.hasSignatureProcessStarted)),
        type,
      }
    },
    manualSignBtn() {
      return {
        show: this.ctaBtn.type === 'sign',
        disabled:
          this.isContinueForbidden ||
          this.isLoading ||
          (this.isLastStep && !this.isContractModelValid) ||
          this.offer.status === OFFER_STATUS.SIGNED,
      }
    },
    stepperComponents() {
      return this.offer.type === OFFER_TYPE.F
        ? STEPPER_PHANTOM_COMPONENTS
        : this.offer.type === OFFER_TYPE.A || this.isTypeBroker
          ? STEPPER_BASE_COMPONENTS
          : STEPPER_ADVANCED_COMPONENTS
    },
    isMainContactNifMissing() {
      return this.offer.requiresMainContactNif && !this.offer.client.clientContact.mainContactNif
    },
    offerTypeByValidations() {
      return calculateOfferTypeByValidations(this.offer)
    },
    shouldApplyValidations() {
      return !this.hasSignatureProcessStarted && !this.isLastStep && this.isStep1ReadOnly
    },
  },
  methods: {
    ...mapActions('offer', [
      SET_OFFER_TARIFF_MODEL,
      UPDATE_AND_SEND_OFFER,
      UPDATE_OFFER_BASEPRICE_ITEM,
      UPDATE_OFFER_DISCOUNT,
      UPDATE_OFFER,
      RESET_OFFER,
      SEND_CONTRACT_TO_SIGN,
      MANUAL_SIGN,
      REQUEST_CONTRACT_STATUS,
      REQUEST_OFFER_DATA,
      CANCEL_EDIT_VALIDATED_OFFER,
      CONFIRM_EDIT_VALIDATED_OFFER,
    ]),
    ...mapMutations('offer', [SET_OFFER_ITEM, SET_TYPE_AND_VAL_STATUS_OFFER]),
    ...mapActions('alerts', ['createAlert']),
    ...mapMutations('alerts', ['removeAlert']),
    ...mapMutations('process', [
      'setOfferTypeByValidations',
      'setRateModelIdOnOfferLoad',
      'resetTypeBFormsValidation',
    ]),
    onChangeIsContinueForbidden(newValue) {
      this.isContinueForbidden = newValue
    },
    onChangeIsLoading(newValue) {
      this.isLoading = newValue ?? !this.isLoading
    },
    async sendRateModel() {
      this.isLoading = true
      this.setRateModelIdOnOfferLoad(this.offer.rateModelId)
      await this[SET_OFFER_TARIFF_MODEL]({
        offerId: this.offer.offerId,
        model: this.offer.rateModelId,
        offer: this.offer,
      })
      this.isLoading = false
      //TODO MANAGE SERVER ERROR
    },
    checkRequiredOfferData() {
      const hasRequiredClientData = isSchemaValid(CLIENT_DATA_VALIDATION_SCHEMA, this.offer)
      if (!hasRequiredClientData) {
        this.createAlert({
          id: 'missingRequiredClientData',
          type: 'error',
          contentKey: 'OFFER_REQUIRED_CLIENT_DATA_ERROR',
        })

        return false
      }

      if (this.isMainContactNifMissing) {
        this.createAlert({
          id: 'missingMainContactNif',
          type: 'error',
          contentKey: 'OFFER_REQUIRED_MAIN_CONTACT_NIF_ERROR',
        })

        return false
      }

      const selectedPaymentMethod = this.offer.paymentMethods.find(method => method.isSelected)?.id
      const isPaymentDebit = DEBIT_PAYMENT_METHODS.includes(selectedPaymentMethod)
      if (isPaymentDebit && !isSchemaValid(DEBIT_BANK_DATA_VALIDATION_SCHEMA, this.offer)) {
        this.createAlert({
          id: 'missingDebitRequiredBankData',
          type: 'error',
          contentKey: 'OFFER_REQUIRED_BANK_DATA_DEBIT_ERROR',
        })

        return false
      }

      const acceptsRefunds = this.offer.additionalServices.numberShipmentRepayments > 0
      if (acceptsRefunds && !isSchemaValid(REFUND_BANK_DATA_VALIDATION_SCHEMA, this.offer)) {
        this.createAlert({
          id: 'missingRefundRequiredBankData',
          type: 'error',
          contentKey: 'OFFER_REQUIRED_BANK_DATA_REFUND_ERROR',
        })

        return false
      }

      return true
    },
    async sendContract() {
      this.isLoading = true

      const hasRequiredOfferData = this.checkRequiredOfferData()
            if (hasRequiredOfferData) {
        const response = await this[SEND_CONTRACT_TO_SIGN]({
          offerId: this.offer.offerId,
          contract: this.pdfFormData,
          version: this.offer.version,
        })

        if ('error' in response) {
          this.createAlert({
            id: 'signRequestError',
            type: 'error',
            contentKey: 'STEPPER_SAVE_CRM_ERROR_ALERT_MESSAGE',
            details: response.error,
          })
        } else {
          this.removeAlert('signRequestError')

          await this[REQUEST_CONTRACT_STATUS]({
            offerId: this.offer.offerId,
          })
        }
      }

      this.isLoading = false
    },
    async saveAndSendOffer() {
      this.isLoading = true
      const offerData = { ...this.offer, step: this.stepsLength }

      if([
        "PDF_VENTA_DIRECTA_2024",
        "PDF_VENTA_DIRECTA_2025",
        "PDF_VENTA_DIRECTA_2026",
        "PDF_VENTA_DIRECTA_2026_V2"
      ].includes(this.contractModel)) {
        offerData.productList = offerData.productList.filter(product => product.id !== "C14E")
        const product24h = offerData.productList.find(product => product.id === "24H")
        const premium = {...product24h}
        premium.id = "C14E"
        premium.desc_key = "ENTERPRISE_PREMIUM"
        premium.pricingRules = {...product24h.pricingRules}
        premium.pricingRules.pivotProductRules = []
        offerData.productList.push(premium)
      }
      

      const responseStatus = await this[UPDATE_AND_SEND_OFFER]({
        offerId: this.offer.offerId,
        offerData,
      })
  
      // Si la respuesta es exitosa, actualizar los datos de la oferta desde el backend
      // para asegurar que el campo finalVersion esté actualizado
      if (!('error' in responseStatus) && responseStatus.status === 200) {
        await this[REQUEST_OFFER_DATA]({
          offerId: this.offer.offerId,
        })
      }
  
      this.isLoading = false
      return responseStatus
    },
    prevStep() {
      this.decreaseOfferStep()
      window.scrollTo({ top: 0, left: 0 })
    },
    handleCtaBtnClick() {
      if (this.isLastStep) {
        this.sendContract()
      } else if (this.isPenultimateStep && !this.hasSignatureProcessStarted && !this.isTypeFOffer) {
        // Mostrar modal de confirmación antes de finalizar la oferta
        this.finalizeOfferDialog = true
      } else {
        this.nextStep()
      }
    },
    proceedWithFinalization() {
      // Ocultar el diálogo y proceder con la finalización
      this.finalizeOfferDialog = false
      this.nextStep()
    },
    async handleManualSignBtnClick() {
      this.manualSignDialog = false

      const hasRequiredOfferData = this.checkRequiredOfferData()
      if (hasRequiredOfferData) {
        const response = await this[MANUAL_SIGN]({
          offerId: this.offer.offerId,
          contract: this.pdfFormData,
        })

        if ('error' in response) {
          this.createAlert({
            id: 'manualSignError',
            type: 'error',
            contentKey: 'STEPPER_MANUAL_SIGN_ERROR_ALERT_MESSAGE',
            details: response.error,
          })
        } else {
          this.removeAlert('manualSignError')

          this.createAlert({
            id: 'manualSignSuccess',
            type: 'success',
            contentKey: 'STEPPER_MANUAL_SIGN_SUCCESS_ALERT_MESSAGE',
            autoHide: true,
          })

          this[REQUEST_OFFER_DATA]({
            offerId: this.offer.offerId,
          })
        }
      }
    },
    async nextStep() {
      if (!this.canEditOffer) {
        this.increaseOfferStep()
        window.scrollTo({ top: 0, left: 0 })
        return
      }

    // Si es el penúltimo paso y debe enviar la oferta, o si debe enviar oferta F, mantener el flujo actual
    if ((this.isPenultimateStep && this.shouldSendOffer) || this.shouldSendFOffer) {
        if (this.isTypeF && this.offer.rateModelId !== '' && !this.isStep1ReadOnly)
          await this.sendRateModel()
        const response = await this.saveAndSendOffer()
        if ('error' in response || response.status !== 200) {
          this.createAlert({ id: 'saveOfferError', type: 'error', contentKey: 'STEPPER_SAVE_ERROR_ALERT_MESSAGE', details: response.error })
        } else {
          this.createAlert({ id: 'saveOfferSuccess', type: 'success', autoHide: true })
          this.increaseOfferStep()
        }
        return
      }

      // Para el paso 1, mantener la lógica original
      if (this.offer.step === 1) {
        if (this.offer.rateModelId !== '' && !this.isStep1ReadOnly) {
          this.sendRateModel()
          // automatic offer.step increase through the getOffer resp
        } else {
          // Guardar oferta antes de avanzar
          await this.saveOffer()
          // Solo avanzar si no hay error (usando el getter alertById del store)
          if (!this.$store.getters['alerts/alertById']('saveOfferError')) {
            this.increaseOfferStep()
          }
        }
      } else if (this.offer.step === 4 && !this.isTypeAOffer) {
        // Guardar oferta antes de mostrar el diálogo
        await this.saveOffer(false)
        // Solo mostrar el diálogo si no hay error
        if (!this.$store.getters['alerts/alertById']('saveOfferError')) {
          this.infoRequiredDialog = true
        }
      } else {
        // Para el resto de pasos, guardar oferta antes de avanzar
        await this.saveOffer(false)
        // Solo avanzar si no hay error
        if (!this.$store.getters['alerts/alertById']('saveOfferError')) {
          this.increaseOfferStep()
        }
      }

      // Scroll al inicio solo si no hay error de guardado
      if (!this.$store.getters['alerts/alertById']('saveOfferError')) {
        window.scrollTo({ top: 0, left: 0 })
      }
    },
    closeInfoRequiredDialog(wantToContinue) {
      wantToContinue && this.increaseOfferStep()
      this.infoRequiredDialog = false
    },
    increaseOfferStep() {
      this.updateOfferStep(1)
    },
    decreaseOfferStep() {
      this.isContinueForbidden = false
      this.updateOfferStep(-1)
    },
    updateOfferStep(stepModifier) {
      const indexCurrentStep = this.steps.indexOf(this.offer.step)
      const newIndex = indexCurrentStep + stepModifier
      if (newIndex >= 0 && newIndex < this.steps.length) {
        this[SET_OFFER_ITEM]([{ name: 'step', value: this.steps[newIndex] }])
      }
    },
    
    async saveOffer(showSuccessAlert = false) {
      
      // Código original para productos especiales
      if ([
        "PDF_VENTA_DIRECTA_2024",
        "PDF_VENTA_DIRECTA_2025",
        "PDF_VENTA_DIRECTA_2026",
        "PDF_VENTA_DIRECTA_2026_V2"
      ].includes(this.contractModel)) {
        this.offer.productList = this.offer.productList.filter(product => product.id !== "C14E")
        const product24h = this.offer.productList.find(product => product.id === "24H")
        const premium = {...product24h}
        premium.id = "C14E"
        premium.desc_key = "ENTERPRISE_PREMIUM"
        premium.pricingRules = {...product24h.pricingRules}
        premium.pricingRules.pivotProductRules = []
        this.offer.productList.push(premium)
      }

      const statusCode = await this[UPDATE_OFFER]({
        offerId: this.offer.offerId,
        offerData: this.offer,
      })

      if (statusCode === 201) {
        if (showSuccessAlert) {
          this.createAlert({
            id: 'saveOfferSuccess',
            type: 'success',
            contentKey: 'STEPPER_SAVE_SUCCESS_ALERT_MESSAGE',
            autoHide: true,
          })
        }
        await this[REQUEST_OFFER_DATA]({
          offerId: this.offer.offerId,
        })
      } else {
        this.createAlert({
          id: 'saveOfferError',
          type: 'error',
          contentKey: 'STEPPER_SAVE_ERROR_ALERT_MESSAGE',
        })
      }
    },
    async resetOffer() {
      this.resetDialog = false

      const response = await this[RESET_OFFER](this.offer)

      if (response.status === 201) {
        this.createAlert({
          id: 'resetOfferSuccess',
          type: 'success',
          contentKey: 'STEPPER_RESET_SUCCESS_ALERT_MESSAGE',
          autoHide: true,
        })

        await this[REQUEST_OFFER_DATA]({
          offerId: this.offer.offerId,
        })
      } else {
        this.createAlert({
          id: 'resetOfferError',
          type: 'error',
          contentKey: 'STEPPER_RESET_ERROR_ALERT_MESSAGE',
        })
      }
    },
    checkOfferTypeByValidations() {
      this.setOfferTypeByValidations(this.offerTypeByValidations)

      if (this.shouldApplyValidations && this.offer.type !== OFFER_TYPE.C) {
        if (this.offerTypeByValidations.productPrices === OFFER_TYPE.B) {
          this.createAlert({
            id: 'product_prices_type_B',
            type: 'info',
            contentKey: 'ALERT_TYPE_B_PRODUCT_PRICES',
            manualDismiss: true,
          })
        } else {
          this.removeAlert('product_prices_type_B')
        }

        if (this.offerTypeByValidations.productWeights === OFFER_TYPE.B) {
          this.createAlert({
            id: 'product_weights_type_B',
            type: 'info',
            contentKey: 'ALERT_TYPE_B_PRODUCT_WEIGHTS',
            manualDismiss: true,
          })
        } else {
          this.removeAlert('product_weights_type_B')
        }

        if (this.offerTypeByValidations.additionalServices === OFFER_TYPE.B) {
          this.createAlert({
            id: 'additional_services_type_B',
            type: 'info',
            contentKey: 'ALERT_TYPE_B_ADDITIONAL_SERVICES',
            manualDismiss: true,
          })
        } else {
          this.removeAlert('additional_services_type_B')
        }

        if (this.offerTypeByValidations.paymentMethods === OFFER_TYPE.B) {
          this.createAlert({
            id: 'payment_methods_type_B',
            type: 'info',
            contentKey: 'ALERT_TYPE_B_PAYMENT_METHODS',
            manualDismiss: true,
          })
        } else {
          this.removeAlert('payment_methods_type_B')
        }

        if (this.offerTypeByValidations.products === OFFER_TYPE.B) {
          this.createAlert({
            id: 'products_type_B',
            type: 'info',
            contentKey: 'ALERT_TYPE_B_PRODUCTS',
            manualDismiss: true,
          })
        } else {
          this.removeAlert('products_type_B')
        }
      }
    },
    handleCancelEditValidatedOffer() {
      this[CANCEL_EDIT_VALIDATED_OFFER]()
    },
    handleConfirmEditValidatedOffer() {
      this[CONFIRM_EDIT_VALIDATED_OFFER]()
    },
  },
  created() {
    this.setRateModelIdOnOfferLoad(this.offer.rateModelId)
  },
  mounted() {
    this.checkOfferTypeByValidations()
  },
  updated() {
    const servicePointsWithSeveralShipments = this.offer.optyServicePoints.filter(
      servicePoint => servicePoint.compromisedShipments > 0,
    )

    if (this.shouldApplyValidations && servicePointsWithSeveralShipments.length > 1) {
      this.createAlert({
        id: 'optyServicePoints_type_B',
        type: 'info',
        contentKey: 'ALERT_TYPE_B_OPTY_SERVICE_POINTS',
        manualDismiss: true,
      })
    } else {
      this.removeAlert('optyServicePoints_type_B')
    }
  },
  watch: {
    offer: {
      immediate: true,
      deep: true,
      handler() {
        if (this.shouldApplyValidations) {
          const computedOfferType = getComputedOfferType(this.offerTypeByValidations, this.offer)

          if (computedOfferType !== this.offer.type) {
            this[SET_TYPE_AND_VAL_STATUS_OFFER]({ type: computedOfferType })
          }
        }

        if (this.offer.type === OFFER_TYPE.C) {
          this.removeAlert('product_prices_type_B')
          this.removeAlert('product_weights_type_B')
          this.removeAlert('additional_services_type_B')
          this.removeAlert('payment_methods_type_B')
          this.removeAlert('products_type_B')
        } else if (this.offer.type === OFFER_TYPE.A) {
          this.resetTypeBFormsValidation()
        }
      },
    },
    offerTypeByValidations: {
      deep: true,
      handler() {
        this.checkOfferTypeByValidations()
      },
    },
  },
}
</script>

<style scoped lang="scss">
.v-stepper__header {
  flex-wrap: nowrap;
}
</style>
